import { EmailAgentMode } from '@numeo/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks'
import * as agenticHubApi from '../services/agenticHubApi'
import { setAutonomousExpired, setAutonomousStartedAt } from '../store/agenticHubSlice'
import type { AgenticHubState } from '../store/agenticHubSlice'

const WAKEUP_INTERVAL_MS = 10 * 60 * 1000 // 10 minutes
const TICK_INTERVAL_MS = 1000 // 1s countdown tick
/** Fallback when Flipt hasn't returned a value yet (or returned 0). */
const FALLBACK_AUTONOMOUS_MAX_RUN_MS = 5 * 60 * 60 * 1000 // 5 hours

/**
 * Manages the autonomous wakeup timer for fully-autonomous tabs.
 *
 * On mount (or page refresh), calls the wakeup endpoint for each
 * fully-autonomous tab to get `lastRunAt` / `nextRunAt` from the server,
 * so the countdown picks up from where it was — not a fresh 10min.
 *
 * After 5h of continuous autonomous activity, the heartbeat stops scheduling
 * and the tab is marked `autonomousExpired` — both for the BE-reported reason
 * `autonomous_expired` and for the local 5h tick (whichever lands first).
 */
export function useAutonomousWakeup() {
    const tabs = useAppSelector((s: { agenticHub: AgenticHubState }) => s.agenticHub.tabs)
    const autonomousMaxRunMs = useAppSelector((s: { agenticHub: AgenticHubState }) => s.agenticHub.autonomousMaxRunMs)
    const dispatch = useAppDispatch()
    const nextRunMap = useRef<Map<string, number>>(new Map())
    const seedingTabs = useRef<Set<string>>(new Set())
    // Mirror latest tabs + cutoff into refs so the 1s tick reads fresh state without
    // re-creating the interval on every Redux tabs update (loads stream constantly).
    const tabsRef = useRef(tabs)
    tabsRef.current = tabs
    const maxRunMsRef = useRef<number>(autonomousMaxRunMs ?? FALLBACK_AUTONOMOUS_MAX_RUN_MS)
    maxRunMsRef.current = autonomousMaxRunMs ?? FALLBACK_AUTONOMOUS_MAX_RUN_MS
    const [debugCountdowns, setDebugCountdowns] = useState<Record<string, number>>({})

    // Seed new fully-autonomous tabs by asking the server for last run time
    useEffect(() => {
        for (const [tabId, tabState] of Object.entries(tabs)) {
            const isAutonomous = tabState.tab.mode === EmailAgentMode.FullyAutonomous && tabState.tab.status !== 'shutdown'
            // Expired tabs are gated here — if the server says expired (or the FE tick flipped
            // it), don't seed or re-seed. Reset (resumeAutonomousRun) clears the flag and this
            // effect re-fires naturally to seed again.
            if (!isAutonomous || tabState.autonomousExpired) {
                nextRunMap.current.delete(tabId)
                seedingTabs.current.delete(tabId)
                continue
            }
            if (!nextRunMap.current.has(tabId) && !seedingTabs.current.has(tabId)) {
                seedingTabs.current.add(tabId)
                agenticHubApi
                    .autonomousWakeup(tabId)
                    .then((result) => {
                        if (result.autonomousStartedAt) {
                            dispatch(setAutonomousStartedAt({ tabId, autonomousStartedAt: result.autonomousStartedAt }))
                        }
                        const nextRunAt = result.nextRunAt ? new Date(result.nextRunAt).getTime() : Date.now() + WAKEUP_INTERVAL_MS
                        nextRunMap.current.set(tabId, nextRunAt)
                    })
                    .catch(() => {
                        nextRunMap.current.set(tabId, Date.now() + WAKEUP_INTERVAL_MS)
                    })
                    .finally(() => seedingTabs.current.delete(tabId))
            }
        }
    }, [tabs, dispatch])

    const fireWakeup = useCallback(
        async (tabId: string) => {
            try {
                const result = await agenticHubApi.autonomousWakeup(tabId)
                if (result.autonomousStartedAt) {
                    dispatch(setAutonomousStartedAt({ tabId, autonomousStartedAt: result.autonomousStartedAt }))
                }
                const nextRunAt = result.nextRunAt ? new Date(result.nextRunAt).getTime() : Date.now() + WAKEUP_INTERVAL_MS
                nextRunMap.current.set(tabId, nextRunAt)
            } catch {
                nextRunMap.current.set(tabId, Date.now() + 60_000)
            }
        },
        [dispatch]
    )

    // Active (non-expired) fully-autonomous tabs — drives effect re-run when mode flips
    // OR when the last tab expires. Excluding expired tabs tears down the 1Hz interval
    // once every tab is paused; otherwise the loop runs every second forever doing nothing.
    const activeAutoTabCount = Object.values(tabs).filter((t) => t.tab.mode === EmailAgentMode.FullyAutonomous && !t.autonomousExpired).length

    useEffect(() => {
        if (activeAutoTabCount === 0) return
        const interval = setInterval(() => {
            const now = Date.now()
            const countdowns: Record<string, number> = {}

            // Local cutoff — check before scheduling. Catches expiry between BE heartbeats
            // so the dispatcher sees the banner the moment the window closes, not 10 min later.
            // Window is Flipt-configurable per user (agentic-hub-autonomous-max-run-hours).
            const cutoff = maxRunMsRef.current
            for (const [tabId, tabState] of Object.entries(tabsRef.current)) {
                if (tabState.tab.mode !== EmailAgentMode.FullyAutonomous) continue
                if (tabState.autonomousExpired) continue
                if (!tabState.autonomousStartedAt) continue
                const elapsed = now - new Date(tabState.autonomousStartedAt).getTime()
                if (elapsed > cutoff) {
                    dispatch(setAutonomousExpired({ tabId, expired: true }))
                    nextRunMap.current.delete(tabId)
                }
            }

            if (nextRunMap.current.size === 0) {
                // Guard against per-second re-renders in the steady state after expiry:
                // a fresh `{}` every tick is never Object.is-equal to the last, so the
                // host component would re-render every second forever. Only flip state
                // when there's actually a stale countdown to clear.
                setDebugCountdowns((prev) => (Object.keys(prev).length === 0 ? prev : {}))
                return
            }

            for (const [tabId, nextRun] of nextRunMap.current.entries()) {
                const remaining = Math.max(0, Math.ceil((nextRun - now) / 1000))
                countdowns[tabId] = remaining

                if (remaining === 0) {
                    nextRunMap.current.set(tabId, now + WAKEUP_INTERVAL_MS)
                    fireWakeup(tabId)
                }
            }

            setDebugCountdowns(countdowns)
        }, TICK_INTERVAL_MS)

        return () => clearInterval(interval)
    }, [fireWakeup, activeAutoTabCount, dispatch])

    return { debugCountdowns }
}
